/* eslint-disable no-underscore-dangle */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  PortableText,
  PortableTextComponentProps,
  PortableTextReactComponents,
} from '@portabletext/react';
import { VideoJS } from '../PrintingSetup/VideoJS';
import {
  BlockValue,
  ContentChild,
  S3AssetValue,
  TableValue,
  PortableTextRendererProps,
  S3AssetLinkMark,
} from './types';

const headingStyles: Record<string, string> = {
  h1: 'text-4xl font-medium my-6 nu-text-base-primary',
  h2: 'text-3xl font-medium mt-12 mb-2 nu-text-base-primary',
  h3: 'text-2xl font-medium mt-8 mb-2 nu-text-base-primary',
  h4: 'text-xl font-medium mt-6 mb-2 nu-text-base-primary',
  h5: 'text-lg font-medium my-2 nu-text-base-primary',
  h6: 'text-base font-medium my-2 nu-text-base-primary',
};

export function PortableTextRenderer({
  value,
  userId,
}: PortableTextRendererProps) {
  const components: Partial<PortableTextReactComponents> = {
    types: {
      block: ({
        value: blockValue,
      }: PortableTextComponentProps<BlockValue>) => {
        const { style = 'normal', articleLinks = [] } = blockValue;

        const renderContent = (
          node: BlockValue['children'][0],
        ): React.JSX.Element | string => {
          if (node.text === '\n') {
            return <br />;
          }

          let content: string | React.JSX.Element = node.text;

          if (node.marks && node.marks.length > 0) {
            if (node.marks.includes('strong')) {
              content = (
                <strong className="font-semibold !font-semibold">
                  {content}
                </strong>
              );
            }
            if (node.marks.includes('em')) {
              content = <em>{content}</em>;
            }
            if (node.marks.includes('code')) {
              content = (
                <code className="bg-gray-100 px-1 rounded font-mono">
                  {content}
                </code>
              );
            }
            if (node.marks.includes('underline')) {
              content = <u>{content}</u>;
            }
            if (node.marks.includes('strike-through')) {
              content = <s>{content}</s>;
            }

            const articleLinkMark = articleLinks?.find((link) =>
              node.marks?.includes(link._key),
            );

            if (articleLinkMark) {
              const { articleId, sectionTitle } = articleLinkMark;

              if (sectionTitle && articleId) {
                const sectionTitleLower = sectionTitle.toLowerCase();

                content = (
                  <Link
                    to={`/users/${userId || ''}/help-center/${sectionTitleLower}/${articleId}`}
                    className="font-semibold underline text-primary-700"
                  >
                    {content}
                  </Link>
                );
              }
            }

            const s3AssetLinkMark = blockValue.markDefs?.find(
              (mark) =>
                mark._type === 's3AssetLink' && node.marks?.includes(mark._key),
            ) as S3AssetLinkMark | undefined;

            if (s3AssetLinkMark?.asset) {
              const { asset } = s3AssetLinkMark;
              const fileName = asset.fileName?.toLowerCase() || '';
              const fileType = asset.fileType?.toLowerCase() || '';
              const isPdf =
                fileType === 'application/pdf' || fileName.endsWith('.pdf');
              const isSpreadsheet =
                fileType.includes('spreadsheet') ||
                fileType.includes('excel') ||
                fileType.includes('csv') ||
                fileName.endsWith('.xlsx') ||
                fileName.endsWith('.xls') ||
                fileName.endsWith('.csv') ||
                fileName.endsWith('.ods') ||
                fileName.endsWith('.numbers');

              if (isPdf || isSpreadsheet) {
                content = (
                  <a
                    href={asset.viewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold underline text-primary-700"
                  >
                    {content}
                  </a>
                );
              }
            }
          }

          return content;
        };

        const content = blockValue.children.map(
          (child: ContentChild, index) => (
            <React.Fragment key={child._key || `child-${index}`}>
              {renderContent(child)}
            </React.Fragment>
          ),
        );

        if (typeof style === 'string' && style in headingStyles) {
          return React.createElement(
            style,
            { className: headingStyles[style] },
            <div className="inline">{content}</div>,
          );
        }

        if (blockValue.listItem === 'number') {
          return <li className="ml-4">{content}</li>;
        }

        if (blockValue.listItem === 'bullet') {
          return <li className="ml-4">{content}</li>;
        }

        return <p className="my-2 leading-[18px]">{content}</p>;
      },

      s3Asset: ({
        value: assetValue,
      }: PortableTextComponentProps<S3AssetValue>) => {
        if (!assetValue || !assetValue.fileType) return null;

        const fileName = assetValue.fileName?.toLowerCase() || '';
        const fileType = assetValue.fileType.toLowerCase();

        const isPdf =
          fileType === 'application/pdf' || fileName.endsWith('.pdf');

        const isSpreadsheet =
          fileType.includes('spreadsheet') ||
          fileType.includes('excel') ||
          fileType.includes('csv') ||
          fileName.endsWith('.xlsx') ||
          fileName.endsWith('.xls') ||
          fileName.endsWith('.csv') ||
          fileName.endsWith('.ods') ||
          fileName.endsWith('.numbers');

        if (isPdf || isSpreadsheet) {
          return (
            <div className="my-4 flex items-center">
              <a
                href={assetValue.viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-primary-700 hover:text-primary-800 font-medium"
              >
                <span>
                  {assetValue.fileName ||
                    (isPdf ? 'Download PDF' : 'Download Spreadsheet')}
                </span>
              </a>
            </div>
          );
        }

        if (fileType.startsWith('image/')) {
          return (
            <div className="my-8">
              <img
                src={assetValue.viewUrl || assetValue.readUrl}
                alt={assetValue.alt || assetValue.fileName || ''}
                className="max-w-full h-auto"
              />
            </div>
          );
        }

        if (fileType.startsWith('video/')) {
          const videoJsOptions = {
            autoplay: false,
            controls: true,
            responsive: true,
            fluid: true,
            sources: [
              {
                src: assetValue.viewUrl || assetValue.readUrl || '',
                type: assetValue.fileType,
              },
            ],
          };

          return (
            <div className="my-8">
              <VideoJS options={videoJsOptions} />
            </div>
          );
        }

        return null;
      },

      table: ({
        value: tableValue,
      }: PortableTextComponentProps<TableValue>) => {
        if (!tableValue || !tableValue.rows || tableValue.rows.length === 0) {
          return null;
        }
        const headerRow = tableValue.rows[0];
        const dataRows = tableValue.rows.slice(1);

        return (
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  {headerRow.cells.map((headerText) => (
                    <th
                      key={`${tableValue._key}-header-${headerText.replace(/\s+/g, '-')}`}
                      className="px-4 py-3 text-left text-sm font-medium text-gray-700 border-b border-gray-200 bg-gray-100"
                    >
                      {headerText}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dataRows.map((row) => {
                  const rowKey = `${tableValue._key}-row-${row.cells.join('').substring(0, 30).replace(/\s+/g, '-')}`;
                  return (
                    <tr key={rowKey} className="hover:bg-gray-50">
                      {row.cells.map((cell, cellIndex) => {
                        const cellKey = `${tableValue._key}-cell-${headerRow.cells[cellIndex]?.replace(/\s+/g, '-')}-${cell.substring(0, 15).replace(/\s+/g, '-')}`;
                        return (
                          <td
                            key={cellKey}
                            className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200 align-top break-words whitespace-normal leading-6"
                          >
                            {cell}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      },
    },

    marks: {
      strong: ({ children }) => (
        <strong className="font-semibold !font-semibold">{children}</strong>
      ),
      em: ({ children }) => <em className="italic">{children}</em>,
      code: ({ children }) => (
        <code className="bg-gray-100 px-1 rounded font-mono">{children}</code>
      ),
      underline: ({ children }) => <u className="underline">{children}</u>,
      'strike-through': ({ children }) => (
        <s className="line-through">{children}</s>
      ),
    },

    list: {
      number: ({ children }) => (
        <ol className="list-decimal ml-6 my-4 mb-0.5">{children}</ol>
      ),
      bullet: ({ children }) => (
        <ul className="list-disc ml-6 my-4 mb-0.5">{children}</ul>
      ),
    },
  };

  return <PortableText value={value || []} components={components} />;
}
